import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useUpdateBillingUnit } from '@dhl/mdp-api'
import { Box } from '@dhl-ui/components'

import { BackButton } from '~/temp-ds-component/BackButton'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { BillingunitForm, CancelDialog } from '~/modules/billingunits/components'
import { useBillingUnit } from '~/modules/billingunits/hooks'
import {
  type BillingUnit,
  convertToBillingUnitDto,
  createBillingUnitValidationSchema,
} from '~/modules/billingunits/model'

type Params = {
  id: string
}

export const BillingunitEditPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<Params>()
  const { t } = useTranslation()
  const { showError, showSuccess } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)
  const { billingUnit } = useBillingUnit(id)
  const { mutate: updateBillingUnit } = useUpdateBillingUnit()
  const billingUnitValidationSchema = createBillingUnitValidationSchema(t)

  const onBack = (dirty: boolean) => (dirty && id ? setCancelDialogOpen(true) : navigate(links.billingUnitsLink()))

  const onSubmit = (billingUnit: BillingUnit) => {
    const billingUnitDto = convertToBillingUnitDto({ ...billingUnit, id: id! })
    updateBillingUnit(
      { id: id!, data: billingUnitDto },
      {
        onSuccess: () => {
          showSuccess(t('admin.billingUnitsPage.editBillingunitSuccess'))
          navigate(links.billingUnitsLink())
        },
        onError: (response) => {
          showError(response?.message ?? t('admin.billingUnitsPage.editBillingunitError'))
        },
      }
    )
  }

  return billingUnit ? (
    <>
      <Formik
        initialValues={billingUnit}
        validateOnBlur
        validateOnMount
        validationSchema={billingUnitValidationSchema}
        onSubmit={onSubmit}
      >
        {({ dirty }) => (
          <Box p={3}>
            <Box display="flex" justifyContent="space-between" pb={2}>
              <BackButton onClick={() => onBack(dirty)}>{t('admin.billingUnitsPage.backButton')}</BackButton>
            </Box>
            <Box pl={2}>
              <BillingunitForm />
            </Box>
          </Box>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  ) : null
}
